import React, { useCallback, useEffect, useMemo } from 'react'
import { Button, Flex, Text, useDisclosure } from '@chakra-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { useNavigate } from 'react-router-dom'
import keys from 'constants/keys'
import colors from 'constants/colors'
import LocaleText from 'components/LocaleText'
import { RankingSection } from './RankingSection'
import { RankingsListSkeleton } from './RankingsListSkeleton'
import { PAGE_TITLE_HEIGHT } from 'components/PageTitle'
import { isSetupAllowedForUser, STEPS } from 'utils/setupStorage'
import { useSetup } from 'contexts/SetupContext'
import { getRanking } from 'redux/actions/ranking'
import AllKpisModal from 'components/Ranking/AllKpisModal'
import AllStoresModal from 'components/Ranking/AllStoresModal'
import { Button as UiButton } from 'components/ui'

const RankingsList = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isOpen: isAllKpisOpen, onOpen: onAllKpisOpen, onClose: onAllKpisClose } = useDisclosure()
  const { isOpen: isLeaderboardOpen, onOpen: onLeaderboardOpen, onClose: onLeaderboardClose } = useDisclosure()
  const {
    isConfigLoading,
    isConfigReady,
    activeGroupID,
    config: { retailUsersObject, mappedRetailUsersByGroups, groups },
  } = useSelector((state: RootState) => state.config)
  const uid = useSelector((state: RootState) => state.auth.uid)
  const theme = useSelector((state: RootState) => state?.general?.theme)
  const { kpisList, kpisMediaData, isRankingLoading, rankingReports, isAllKpisLoading, hasAllKpisData } = useSelector(
    (state: RootState) => state.ranking
  )

  const isStore = !!(activeGroupID && groups[activeGroupID]?.child?.length === 0)

  const competitionKpis = useMemo(() => {
    return Object.values(kpisList).filter((kpi: any) => kpi && kpi.id && kpi.type === 'competition_kpi')
  }, [kpisList])

  const dynamicKpis = useMemo(() => {
    return Object.values(kpisList).filter((kpi: any) => kpi && kpi.id && kpi.type === 'dynamic_kpi')
  }, [kpisList])

  const allKpis = useMemo(() => {
    return [...competitionKpis, ...dynamicKpis]
  }, [competitionKpis, dynamicKpis])

  const handleKpiClick = (kpiId: string) => {
    navigate(keys.ROUTE_NAMES.RANKING(kpiId))
  }

  const onLoadAllKpis = useCallback(() => {
    if (!hasAllKpisData) {
      dispatch(getRanking({ kpis_data: true, kpis_list: true, force_all_competition_data: true }))
    }
    onAllKpisOpen()
  }, [hasAllKpisData, dispatch, onAllKpisOpen])

  const onLoadLeaderboard = useCallback(() => {
    if (!hasAllKpisData) {
      dispatch(getRanking({ kpis_data: true, kpis_list: true, force_all_competition_data: true }))
    }
    onLeaderboardOpen()
  }, [hasAllKpisData, dispatch, onLeaderboardOpen])

  const { isStepCompleted } = useSetup()
  const isCurrentStepCompleted = isStepCompleted(STEPS.RANKINGS_LIST)
  const blockContent = !isCurrentStepCompleted && isSetupAllowedForUser(uid)

  useEffect(() => {
    if (!isStepCompleted(STEPS.RANKINGS_LIST)) {
      setTimeout(() => {
        const event = new CustomEvent('openSetupModal', {
          detail: { stepId: STEPS.RANKINGS_LIST, stepTitle: 'Rankings List' },
        })
        window.dispatchEvent(event)
      }, 100)
    }
  }, [blockContent, isStepCompleted])

  if ((isRankingLoading || isConfigLoading || !isConfigReady) && blockContent) {
    return <RankingsListSkeleton />
  }

  return (
    <Flex
      flexDirection="column"
      w="100%"
      px={['12px', '30px', '36px']}
      py={['12px', '12px', '16px']}
      flexGrow={1}
      height={`calc(100vh - ${PAGE_TITLE_HEIGHT}px)`}
      overflowY="auto"
      overflowX="hidden"
      style={{
        filter: blockContent ? 'blur(10px)' : 'blur(0px)',
        transition: 'filter 0.8s ease-out',
      }}
      pointerEvents={blockContent ? 'none' : 'auto'}
      onScroll={(e) => {
        e.preventDefault()
      }}
    >
      {isStore && (
        <Flex justifyContent="flex-end" mb="12px">
          <Button
            fontSize="14px"
            bg="white"
            borderRadius="5px"
            height="2rem"
            boxShadow="0 2px 4px 0 rgba(0,0,0,0.05)"
            onClick={onLoadAllKpis}
          >
            Load All KPIs
          </Button>
        </Flex>
      )}
      {allKpis && allKpis.length > 0 ? (
        <Flex flexDirection="column" w="100%">
          <RankingSection
            title="competitions"
            headerAction={
              <UiButton
                variant="ghost"
                fontSize={['13px', '14px', '15px']}
                color={theme?.elementsColor ?? colors.blackLight}
                onClick={onLoadLeaderboard}
              >
                <LocaleText text="show_all" />
              </UiButton>
            }
            kpis={competitionKpis}
            kpisMediaData={kpisMediaData}
            rankingReports={rankingReports}
            mappedRetailUsersByGroups={mappedRetailUsersByGroups}
            retailUsersObject={retailUsersObject}
            isDynamicKpi={false}
            onKpiClick={handleKpiClick}
          />

          <RankingSection
            title="kpis"
            kpis={dynamicKpis}
            kpisMediaData={kpisMediaData}
            rankingReports={rankingReports}
            mappedRetailUsersByGroups={mappedRetailUsersByGroups}
            retailUsersObject={retailUsersObject}
            isDynamicKpi={true}
            onKpiClick={handleKpiClick}
          />
        </Flex>
      ) : (
        <Flex justifyContent="center" alignItems="center" minH="400px" flexDirection="column">
          <Text fontSize="24px" color={colors.greyDark} textAlign="center">
            <LocaleText text="ranking_no_data_message" />
          </Text>
        </Flex>
      )}
      <AllKpisModal isOpen={isAllKpisOpen} onClose={onAllKpisClose} />
      <AllStoresModal isOpen={isLeaderboardOpen} onClose={onLeaderboardClose} />
    </Flex>
  )
}

export default RankingsList
